import {
	createContext,
	type ReactNode,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { useRequiredContext } from 'hooks';
import type { OIDCError } from 'utils/business-logic';

import { useInitialQueryValueContext } from './InitialQueryValueContextProvider';

interface AuthStatusContextProviderType {
	code?: string;
	isSuccessfulLogin: boolean;
	loginError?: OIDCError;
	resetCode: () => void;
	resetLoginError: () => void;
	resetSignUpError: () => void;
	shouldInitialOpenLogin: boolean;
	shouldInitialOpenSignupForm: boolean;
	signUpError?: OIDCError;
}

export const AuthStatusContext = createContext<
	AuthStatusContextProviderType | undefined
>(undefined);

interface Props {
	children: ReactNode;
}

export function AuthStatusContextProvider({ children }: Props) {
	const initialQueryValueContext = useInitialQueryValueContext();

	const hasError = Boolean(initialQueryValueContext.error);
	const isLogin = initialQueryValueContext.isLogin === 'true';
	const isSignup = initialQueryValueContext.isLogin === 'false';
	const hasLoginError = hasError && isLogin;
	const hasSignUpError = hasError && isSignup;

	const [loginError, setLoginError] = useState(
		hasLoginError ? initialQueryValueContext.error : undefined,
	);
	const [signUpError, setSignUpError] = useState(
		hasSignUpError ? initialQueryValueContext.error : undefined,
	);

	const [code, setCode] = useState(initialQueryValueContext.code);

	const resetLoginError = useCallback(() => {
		setLoginError(undefined);
	}, [setLoginError]);

	const resetSignUpError = useCallback(() => {
		setSignUpError(undefined);
	}, [setSignUpError]);

	const resetCode = useCallback(() => {
		setCode(undefined);
	}, [setCode]);

	const value = useMemo(
		() => ({
			code,
			isSuccessfulLogin: isLogin && !hasLoginError,
			loginError,
			resetCode,
			resetLoginError,
			resetSignUpError,
			shouldInitialOpenLogin: hasLoginError,
			shouldInitialOpenSignupForm: Boolean(code) || hasSignUpError,
			signUpError,
		}),
		[
			code,
			hasLoginError,
			hasSignUpError,
			isLogin,
			loginError,
			resetCode,
			resetLoginError,
			resetSignUpError,
			signUpError,
		],
	);

	return <AuthStatusContext value={value}>{children}</AuthStatusContext>;
}

AuthStatusContextProvider.displayName = 'AuthStatusContextProvider';

export function useAuthStatusContext() {
	return useRequiredContext(AuthStatusContext, 'useAuthStatusContext');
}
